import type { FC } from 'react'

import type { WhiteLabel } from '@skytech/types'
import { Input, Select } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Td } from '@skytech/ui/Table/Td'
import {
  DECIMAL_PATTERN,
  getWhiteLabelForDealerStyle,
  MAX_DECIMAL_12_4,
  MIN_PRICE,
  trimLeadingZeros,
} from '@skytech/utils'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import Highlight from '@/components/util/Highlight'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useLocalDate } from '@/hooks/use-local-date'
import { useProfile } from '@/hooks/user'

import type { TradeInOrder } from '../resources'

export const OrderListItem: FC<
  {
    searchFilter: string
    index: number
    handleOnFinish: (id: string) => void
  } & TradeInOrder
> = ({
  searchFilter,
  index,
  handleOnFinish,
  productName,
  orderStatus,
  assetDate,
  grade,
  estimatedPrice,
  finalGrade,
  finalPrice,
  imeiSerialNumber,
  customerName,
  orderId,
}) => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()
  const { formatNumber } = useFormattedNumberWithCurrency()
  const { formatDate } = useLocalDate()
  const [queryStatus] = useSearchParamsByKey('status', 'PRICE_REQUEST')
  const currency = useProfile()?.currency
  const isPriceRequestStatus = queryStatus === 'PRICE_REQUEST'
  const isSentStatus = queryStatus === 'SENT'

  const estimatedPriceAmount =
    typeof estimatedPrice === 'string' ? Number(estimatedPrice) : estimatedPrice
  const finalPriceAmount = typeof finalPrice === 'string' ? Number(finalPrice) : finalPrice

  return (
    <tr>
      <Td>
        <div className="flex flex-col">
          <Highlight pattern={searchFilter} text={productName || ''} />
          <span className="mt-1 text-xs text-zinc-500">
            <Highlight pattern={searchFilter} text={imeiSerialNumber || ''} />
          </span>
          <span className="mt-1 text-xs text-zinc-500">
            <Highlight pattern={searchFilter} text={customerName || ''} />
          </span>
        </div>
      </Td>
      <Td className="hidden md:table-cell">
        <Highlight pattern={searchFilter} text={formatDate({ date: assetDate })} />
      </Td>

      <Td className="center hidden md:table-cell">
        {isPriceRequestStatus ? (
          <GradeDropdown name={`orders[${index}].grade`} />
        ) : (
          <Highlight pattern={searchFilter} text={grade ?? '-'} />
        )}
      </Td>

      <Td
        className={twJoin(
          'hidden w-2/12 md:table-cell',
          isPriceRequestStatus ? 'center' : 'text-end'
        )}
      >
        {isPriceRequestStatus ? (
          <PriceInput
            name={`orders[${index}].estimatedPrice`}
            currency={currency}
            whiteLabel={whiteLabel}
          />
        ) : (
          <Highlight
            pattern={searchFilter}
            text={formatNumber({ amount: estimatedPriceAmount ?? 0 }) ?? '-'}
          />
        )}
      </Td>

      <Td className="center hidden md:table-cell">
        {isSentStatus ? (
          <GradeDropdown name={`orders[${index}].finalGrade`} />
        ) : (
          <Highlight pattern={searchFilter} text={finalGrade ?? '-'} />
        )}
      </Td>

      <Td className={twJoin('hidden md:table-cell', isSentStatus ? 'center' : 'text-end')}>
        {isSentStatus ? (
          <PriceInput
            name={`orders[${index}].finalPrice`}
            currency={currency}
            whiteLabel={whiteLabel}
          />
        ) : (
          <Highlight
            pattern={searchFilter}
            text={formatNumber({ amount: finalPriceAmount ?? 0 }) ?? '-'}
          />
        )}
      </Td>

      <Td className="center">
        <Highlight
          pattern={searchFilter}
          text={translate(`trade_in_order_status/${orderStatus}`)}
        />
        {queryStatus === 'FINAL_PRICING' && (
          <Button
            variant="primary"
            size="small"
            onClick={() => handleOnFinish(orderId)}
            whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
          >
            <Translation id="Finish" />
          </Button>
        )}
      </Td>
    </tr>
  )
}

const gradeOption = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
]
const GradeDropdown: FC<{
  name: string
}> = ({ name }) => (
  <Field name={name}>
    {({ field, form, meta }: FieldProps) => (
      <Select
        className="w-fit"
        id={name}
        options={gradeOption}
        touched={meta.touched}
        error={meta.error}
        marginBottom={16}
        {...field}
        onChange={option => {
          form.setFieldValue(field.name, option?.value)
        }}
      />
    )}
  </Field>
)

const PriceInput: FC<{
  whiteLabel: WhiteLabel
  name: string
  currency?: string
}> = ({ whiteLabel, name, currency }) => (
  <Field name={name}>
    {({ field, meta, form }: FieldProps) => (
      <Input
        isFullWidth
        id={name}
        inputMode="decimal"
        className="rounded-lg p-3"
        LeftElement={<span>{currency}</span>}
        type="text"
        min={String(MIN_PRICE)}
        max={String(MAX_DECIMAL_12_4)}
        error={meta.error}
        touched={meta.touched}
        marginBottom={16}
        {...field}
        value={field.value === null ? '0' : trimLeadingZeros(field.value)}
        onChange={e => {
          let newValue = e.target.value.trim()
          if (!DECIMAL_PATTERN.test(newValue) && newValue !== '') return
          form.setFieldValue(field.name, newValue)
        }}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      />
    )}
  </Field>
)
